import {
  BulkCustomEmailRequest,
  SeriesTestCustomEmailRequest,
  bulkUpdateConfigsEmail,
  bulkUpdateConfigsStudioEmailConfigTemplate,
  createSendCalendarInvite,
  listEmail,
  listEmailConfigForSeries,
  listStudioEmailConfigTemplate,
  partialUpdateEmail,
  updateEmailConfigForEventTemplate,
  patchSeriesEmailTemplate,
  sendCustomEmailEmail,
  sendSeriesCustomEmail,
  sendSeriesTestEmail,
  sendTestEmailEmail,
  bulkUpdateConfigsEmailConfig
} from '@goldcast/api/backend';

import { sortEmailSchedule, getErrorMessages } from './utils';

import { EmailRequestFor } from '@/studio/emailTemplates/store/types';

const actions = {
  async fetchEmailTypes(context, { id, requestFor }: { id: string; requestFor: EmailRequestFor }) {
    if (!id) {
      return;
    }

    context.commit('setLoadingEmailTypesList', true);
    let promise: Promise<any>;
    if (requestFor === 'event') {
      promise = listEmail({
        queryParams: {
          event: id,
          ordering: 'created_at',
          //@ts-ignore
          all_reminders_and_custom: true
        }
      });
    } else if (requestFor === 'event_template') {
      promise = listStudioEmailConfigTemplate({
        queryParams: {
          event_template: id,
          ordering: 'created_at'
        }
      });
    } else if (requestFor === 'series') {
      promise = listEmailConfigForSeries({
        queryParams: {
          series: id
        }
      });
    } else {
      promise = Promise.reject('Failed to fetch email templates');
    }

    return new Promise((resolve, reject) => {
      return promise
        .then(emailTypes => {
          sortEmailSchedule(emailTypes);
          context.commit('setEmailTypes', { emailTypes });
          context.commit('setEmailTypesFor', id);
          resolve(emailTypes);
        })
        .catch(error => {
          const errorMessage = {
            msg: 'Error while loading scheduled emails.',
            mode: 'error'
          };
          context.dispatch('studioToast/triggerToast', errorMessage, {
            root: true
          });
          reject(error);
        })
        .finally(() => {
          context.commit('setLoadingEmailTypesList', false);
        });
    });
  },
  async updateEmailSchedule(
    context,
    {
      emailSchedule,
      requestFor
    }: {
      emailSchedule: { active: boolean; id: string }[];
      requestFor: EmailRequestFor;
    }
  ) {
    context.commit('setLoadingBulkUpdate', true);
    let promise: Promise<any>;
    if (requestFor === 'event') {
      promise = bulkUpdateConfigsEmail({
        queryParams: {},
        body: emailSchedule
      });
    } else if (requestFor === 'event_template') {
      promise = bulkUpdateConfigsStudioEmailConfigTemplate({
        body: emailSchedule
      });
    } else if (requestFor === 'series') {
      promise = bulkUpdateConfigsEmailConfig({
        body: emailSchedule
      });
    } else {
      promise = Promise.reject('Failed to update email schedule');
    }
    return new Promise((resolve, reject) => {
      return promise
        .then(() => {
          context.commit('updateEmailSchedule', emailSchedule);
          resolve(emailSchedule);
        })
        .catch(error => {
          const errorMessage = {
            msg: 'Error while saving email schedule.',
            mode: 'error'
          };
          context.dispatch('studioToast/triggerToast', errorMessage, {
            root: true
          });
          reject(error);
        })
        .finally(() => {
          context.commit('setLoadingBulkUpdate', false);
        });
    });
  },
  async updateEmailType(
    context,
    {
      scheduledEmail,
      requestFor
    }: {
      scheduledEmail: {
        active: boolean;
        calendar_invite?: boolean;
        id: string;
        subject: string;
      };
      requestFor: EmailRequestFor;
    }
  ) {
    context.commit('setLoadingUpdateEmailType', true);
    let promise;
    if (requestFor === 'event') {
      promise = partialUpdateEmail({
        id: scheduledEmail.id,
        body: scheduledEmail
      });
    } else if (requestFor === 'event_template') {
      promise = updateEmailConfigForEventTemplate({
        id: scheduledEmail.id,
        body: scheduledEmail
      });
    } else if (requestFor === 'series') {
      promise = patchSeriesEmailTemplate({
        id: scheduledEmail.id,
        body: scheduledEmail
      });
    } else {
      promise = Promise.reject('Failed to fetch email templates');
    }
    return new Promise((resolve, reject) => {
      return promise
        .then(emailConfig => {
          if (emailConfig.email_type === 'Registration') {
            const regEmailMutationName =
              requestFor === 'event'
                ? 'studioRegistration/setRegistrationEmail'
                : 'studioTemplateList/setRegistrationEmail';
            context.commit(regEmailMutationName, { registrationEmail: emailConfig }, { root: true });
          }
          context.commit('updateEmailConfig', {
            emailConfig
          });
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Email configuration updated successfully.',
              mode: 'success'
            },
            {
              root: true
            }
          );
          resolve(emailConfig);
        })
        .catch(error => {
          const errorMessage = getErrorMessages(error, 'Error updating email configuration.');
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: errorMessage,
              mode: 'error'
            },
            {
              root: true
            }
          );
          reject(errorMessage);
        })
        .finally(() => {
          context.commit('setLoadingUpdateEmailType', false);
        });
    });
  },
  async sendTestEmail(
    context,
    {
      payload,
      requestFor,
      id
    }: { payload: BulkCustomEmailRequest | SeriesTestCustomEmailRequest; requestFor: EmailRequestFor; id: string }
  ) {
    context.commit('setLoadingSendTestEmail', true);

    let promise: Promise<any>;
    if (requestFor === 'series') {
      promise = sendSeriesTestEmail({
        series_id: id,
        body: payload as SeriesTestCustomEmailRequest
      });
    } else {
      promise = sendTestEmailEmail({
        body: payload as BulkCustomEmailRequest
      });
    }

    return new Promise(resolve => {
      return promise
        .then(response => {
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Test email(s) successfully sent',
              mode: 'success'
            },
            {
              root: true
            }
          );
          resolve(response);
        })
        .catch(error => {
          let errorJson;
          try {
            errorJson = JSON.parse(error as string);
          } catch {
            errorJson = undefined;
          }
          const errorMessage = {
            mode: 'error',
            msg: 'Something went wrong. Test email failed to send'
          };
          if (errorJson?.emails && errorJson?.emails[0]?.code === 'does_not_exist') {
            errorMessage.msg = 'Emails must be sent to a registered Goldcast user. Please try again.';
          }
          context.dispatch('studioToast/triggerToast', errorMessage, {
            root: true
          });
        })
        .finally(() => {
          context.commit('setLoadingSendTestEmail', false);
        });
    });
  },
  sendCustomEmail(
    context,
    {
      payload,
      requestFor,
      id
    }: {
      payload: BulkCustomEmailRequest | SeriesTestCustomEmailRequest;
      requestFor: EmailRequestFor;
      id: string;
    }
  ) {
    context.commit('setLoadingSendCustomEmail', true);

    let promise: Promise<any>;
    if (requestFor === 'event') {
      promise = sendCustomEmailEmail({
        body: payload as BulkCustomEmailRequest
      });
    } else if (requestFor === 'series') {
      promise = sendSeriesCustomEmail({
        body: payload as SeriesTestCustomEmailRequest,
        series_id: id
      });
    } else {
      promise = Promise.reject('Failed to send custom email');
    }

    return promise;
  },
  sendUpdatedCalendarInviteEmails(
    context,
    { eventId, onlyAttendees = false, message }: { eventId: string; onlyAttendees?: boolean; message?: string }
  ) {
    return new Promise((resolve, reject) => {
      createSendCalendarInvite({ id: eventId, body: { forced: true, only_attendees: onlyAttendees } })
        .then(() => {
          resolve(message || 'Sent updated calendar invite emails to registrants successfully');
        })
        .catch(reject);
    });
  },
  sendCancelCalendarInvite(
    context,
    { eventId, subject, userIds, message }: { eventId: string; subject: string; userIds?: string[]; message?: string }
  ) {
    return new Promise((resolve, reject) => {
      createSendCalendarInvite({
        id: eventId,
        body: {
          calendar_method: 'CANCEL',
          subject,
          ...(userIds && userIds.length ? { user_ids: userIds } : {})
        }
      })
        .then(() => {
          resolve(message || 'Sent cancellation calendar invites to registrants successfully');
        })
        .catch(reject);
    });
  }
};

export default actions;
